import { useEffect, useId, useRef } from 'react';
import type { WireColor } from '@il-colpo/engine';
import { useReducedMotion } from '../../../systems/motion.ts';
import { Burst } from './Burst.tsx';
import { SWATCH } from './colors.ts';
import styles from './objects.module.css';
import { createWire, settleWire, stepWire, wirePath, type Wire } from './wire.ts';

const TOP = 34;
const BOTTOM = 136;

/** Where wire `i` of `n` is pinned at the top and at the bottom of the plate. */
export function wireEnds(
  i: number,
  n: number,
): { from: { x: number; y: number }; to: { x: number; y: number } } {
  const x = n <= 1 ? 150 : 42 + (i * 216) / (n - 1);
  // The wires cross a little, the way they do behind a real panel.
  const shift = i % 2 === 0 ? 12 : -12;
  return { from: { x, y: TOP }, to: { x: x + (n <= 1 ? 0 : shift), y: BOTTOM } };
}

/**
 * The electrical panel: a steel plate with a row of terminals at each end and soft wires
 * between them that sway on their own and hang slack once they have been dealt with.
 * `color` is null for a role that cannot see the wires.
 */
export function PanelObject({ wires }: { wires: { color: WireColor | null; done: boolean }[] }) {
  const id = useId();
  const reduced = useReducedMotion();
  const outline = useRef<(SVGPathElement | null)[]>([]);
  const strand = useRef<(SVGPathElement | null)[]>([]);
  const doneRef = useRef<boolean[]>([]);
  const n = wires.length;
  const doneKey = wires.map((w) => (w.done ? '1' : '0')).join('');
  // The animation loop reads which wires are done without being restarted when that changes.
  useEffect(() => {
    doneRef.current = [...doneKey].map((c) => c === '1');
  }, [doneKey]);

  useEffect(() => {
    const chains: Wire[] = Array.from({ length: n }, (_, i) => {
      const { from, to } = wireEnds(i, n);
      return createWire(from, to, 9, 1.16 + (i % 3) * 0.04);
    });
    const draw = (): void => {
      chains.forEach((c, i) => {
        const d = wirePath(c);
        outline.current[i]?.setAttribute('d', d);
        strand.current[i]?.setAttribute('d', d);
      });
    };
    if (reduced) {
      for (const c of chains) settleWire(c);
      draw();
      return;
    }
    for (const c of chains) settleWire(c, 0.8);
    draw();
    let frame = 0;
    let last = performance.now();
    let t = 0;
    const tick = (now: number): void => {
      const dt = Math.min(0.05, (now - last) / 1000);
      // About 30 steps a second is plenty for a wire.
      if (dt >= 1 / 32) {
        last = now;
        t += dt;
        chains.forEach((c, i) => {
          const done = doneRef.current[i] === true;
          stepWire(c, dt, {
            wind: done ? 0 : Math.sin(t * 1.3 + i * 1.9) * 140 + Math.sin(t * 3.1 + i) * 45,
            damping: done ? 0.94 : 0.985,
          });
        });
        draw();
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [n, reduced]);

  return (
    <div className={styles['hero']}>
      <svg className={styles['svg']} viewBox="0 0 300 170" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`${id}-plate`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#4b5262" />
            <stop offset="1" stopColor="#2a2f3d" />
          </linearGradient>
        </defs>
        <rect
          x="8"
          y="6"
          width="284"
          height="158"
          rx="10"
          fill={`url(#${id}-plate)`}
          stroke="#7a8496"
          strokeWidth="2"
        />
        {[
          [18, 16],
          [282, 16],
          [18, 154],
          [282, 154],
        ].map(([x, y]) => (
          <g key={`${x}-${y}`}>
            <circle cx={x} cy={y} r="4" fill="#8a929c" stroke="#262b3b" />
            <line
              x1={(x ?? 0) - 2.5}
              y1={(y ?? 0) - 2.5}
              x2={(x ?? 0) + 2.5}
              y2={(y ?? 0) + 2.5}
              stroke="#262b3b"
            />
          </g>
        ))}
        <rect x="28" y="20" width="244" height="14" rx="4" fill="#171b2a" stroke="#5a6476" />
        <rect x="28" y="136" width="244" height="14" rx="4" fill="#171b2a" stroke="#5a6476" />
        {wires.map((w, i) => {
          const { from, to } = wireEnds(i, n);
          const color = w.color ? SWATCH[w.color] : '#6b7388';
          // Dark wires get a pale rim and light ones a dark one, so every wire shows on the plate.
          const dark = w.color === 'black' || w.color === 'blue';
          const rim = dark ? '#aab3c4' : 'var(--c-ink-950)';
          return (
            <g key={i} className={w.done ? styles['wireDone'] : undefined}>
              <path
                ref={(el) => {
                  outline.current[i] = el;
                }}
                fill="none"
                stroke={rim}
                strokeWidth={dark ? 6.5 : 7}
                strokeLinecap="round"
              />
              <path
                ref={(el) => {
                  strand.current[i] = el;
                }}
                fill="none"
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx={from.x} cy="27" r="5" fill="#8a929c" stroke="#262b3b" />
              <circle cx={to.x} cy="143" r="5" fill="#8a929c" stroke="#262b3b" />
              <circle
                className={styles['led']}
                cx={from.x}
                cy="12"
                r="3"
                fill={w.done ? 'var(--c-neon-green)' : '#3a3f52'}
              />
              {w.done ? <Burst key={`cut-${i}`} x={(from.x + to.x) / 2} y={86} /> : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
